<template>
    <Table
        class-name="add-to-catalog-table"
        :value="selected_items"
        :columns="columns"
        :rows="filteredSelectedItems"
        :rows-count="filteredSelectedItems.length"
        :filters="filters"
        :filters-conf="filtersConf"
        :loading="formats_loading"
        :show-select="true"
        :dense="true"
        :remote="false"
        :placeholder-title="$translate('no_product_selected')"
        :placeholder-description="$translate('no_product_selected_description')"
        :hide-default-footer="true"
        item-key="id"
        item-value="id"
        @input="updateSelectedItems"
        @on-row-click="toggleItem"
        @on-filters-change="onFiltersChange"
    >
        <template slot="product" slot-scope="props">
            <div class="product-cell py-2">
                <div class="text-caption grey--text text--darken-1">
                    {{ props.row.upc || "—" }}
                </div>
                <ProductName :product="props.row" />
            </div>
        </template>
        <template slot="catalog" slot-scope="props">
            <AddToCatalogCatalogCell
                :catalog-entries="props.row.catalog_entries || []"
                :product-format-name="productFormatName(props.row)"
            />
        </template>
        <template slot="format" slot-scope="props">
            <AddToCatalogCatalogCell
                :product="props.row"
                :show-catalog-entries="false"
                :show-format-picker="true"
                :format-id="props.row.add_format"
                :format-options-list="formatOptions"
                :formats-loading="formats_loading"
                :format-errors="formatErrors(props.row)"
                :product-format-name="productFormatName(props.row)"
                @format-change="onFormatChange(props.row, $event)"
            />
        </template>
        <template slot="unit_amount" slot-scope="props">
            <v-text-field
                :value="props.row.add_unit_amount"
                :label="unitAmountLabel"
                type="text"
                inputmode="decimal"
                dense
                outlined
                hide-details
                prefix="€"
                class="unit-amount-input"
                @input="onUnitAmountChange(props.row, $event)"
            />
        </template>
    </Table>
</template>

<script>
import { mapState, mapMutations } from "vuex";

import GlobalMixins from "@/mixins/global";
import AddToCatalogCatalogCell from "./addtocatalog-catalog-cell";

export default {
    mixins: [GlobalMixins],
    components: { AddToCatalogCatalogCell },
    data() {
        return {
            filters: {},
        };
    },
    computed: {
        ...mapState("addtocatalog", [
            "catalog",
            "selected_items",
            "formats",
            "formats_loading",
        ]),
        formatOptions() {
            return this.formats || [];
        },
        unitAmountLabel() {
            return this.catalog?.prices_include_vat
                ? this.$translate("sellPriceInclVat")
                : this.$translate("sellPriceExclVat");
        },
        filteredSelectedItems() {
            return this.selected_items.filter((item) => {
                if (this.filters.search) {
                    return `${item.name} ${item.description || ""}`
                        .toLowerCase()
                        .includes(this.filters.search.toLowerCase());
                }
                return true;
            });
        },
        filtersConf() {
            return [
                {
                    preset: "search",
                },
            ];
        },
        columns() {
            return [
                {
                    label: this.$translate("product"),
                    field: "product",
                },
                {
                    label: this.$translate("existingEntries"),
                    field: "catalog",
                    sortable: false,
                },
                {
                    label: this.$translate("format"),
                    field: "format",
                    sortable: false,
                },
                {
                    label: this.unitAmountLabel,
                    field: "unit_amount",
                    sortable: false,
                },
            ];
        },
    },
    methods: {
        ...mapMutations("addtocatalog", [
            "toggleItem",
            "updateSelectedItems",
            "updateSelectedItemFormat",
            "updateSelectedItemUnitAmount",
        ]),
        productInCatalog(product) {
            return (product.catalog_entries || []).length > 0;
        },
        productFormatName(product) {
            return product?.format_data?.name || null;
        },
        formatErrors() {
            return [];
        },
        onFormatChange(product, formatId) {
            this.updateSelectedItemFormat({
                productId: product.id,
                formatId: formatId || null,
            });
        },
        onUnitAmountChange(product, value) {
            this.updateSelectedItemUnitAmount({
                productId: product.id,
                unitAmount: this.$utils.normalizeDecimalString(value) || "",
            });
        },
        onFiltersChange(filters) {
            this.filters = filters;
        },
    },
    i18n: {
        messages: {
            en: {
                product: "Product",
                existingEntries: "Existing entries",
                format: "Format to add",
                sellPriceExclVat: "Sell price (excl. VAT)",
                sellPriceInclVat: "Sell price (incl. VAT)",
                no_product_selected: "No product selected",
                no_product_selected_description: "No product has been selected yet.",
                errors: {
                    formatRequired: "Sale format is required for this product.",
                },
            },
            fr: {
                product: "Produit",
                existingEntries: "Entrées existantes",
                format: "Format à ajouter",
                sellPriceExclVat: "Prix de vente (HT)",
                sellPriceInclVat: "Prix de vente (TTC)",
                no_product_selected: "Aucun produit sélectionné",
                no_product_selected_description:
                    "Aucun produit n'a été sélectionné pour le moment.",
                errors: {
                    formatRequired:
                        "Le format de vente est obligatoire pour ce produit.",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.product-cell {
    min-width: 220px;
}

.unit-amount-input {
    min-width: 150px;
    max-width: 180px;
}
</style>
